import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Info,
  Upload,
  Lock,
  Scan,
  MessageSquare,
  Cable,
  RefreshCw,
} from 'lucide-react';
import { useNotification } from '@/components/notifications';
import { PromptEnhancer } from './prompt-enhancer';
import {
  NodeInputType,
  NodeParam,
  InputValue,
  NodeCategory,
} from '@/types/node-types';
import { useNodeStore } from '@/stores/node-store';
import { useWorkflowStore } from '@/stores/workflow-store';
import { useWorkflowValidationStore } from '@/stores/workflow-validation-store';
import { Dropzone } from '@/components/dropzone';
import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/loading-spinner';
import { uploadFile, isLargeUpload } from '@/services/workflow/upload-file';
import { useUploadStore, useNodeUpload } from '@/stores/use-upload-store';
import {
  PanelUploadField,
  PanelUploadSuccess,
  parseUploadedFile,
} from '@/pages/workflow/component/upload/upload-ui';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select } from '@/components/select';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multi-select';
import { Slider } from '@/components/ui/slider';
// re-enable when publish plug icons are restored
// import Lottie, { type LottieRefCurrentProps } from 'lottie-react';
// import plugClickAnimation from '@/assets/lottieAnimation/Plug-click.json';
// import plugHoverAnimation from '@/assets/lottieAnimation/Plug-hover.json';
import { MCPServersInput } from './mcp-servers-input';
import classNames from 'classnames';
import * as Sentry from '@sentry/react';
import { useOrgStore } from '@/stores/org-store';
import { getNodeList } from '@/services/workflow/get-node-list';
import { getGuardrailsConfigs } from '@/services/guardrails/get-guardrails-configs';
import { useEvalCatalogStore } from '@/stores/eval-catalog-store';
import { useNodeExplorerStore } from '@/stores/node-explorer-store';
import { getStructDisplayLabel } from '@/stores/helpers/node-naming';
import { INLINE_COLLECTION_FIELD_IDS } from '@/constants/collection-scope';
import {
  fetchCollectionScopeOptions,
  applyRefreshedOptions,
  isDatasetField,
} from './collection-scope-helpers';
import type { CustomNode } from '@/types/node-types';
import {
  FILE_INPUT_MEDIA_MAX_SIZE_MB,
  FILE_INPUT_MAX_SIZE_MB,
  FILE_INPUT_ACCEPT,
} from '@/constants/file-upload';

/** Build a human-readable label for a connected field, e.g. "Receiving task from Text Input". */
function buildConnectionLabel(
  displayName: string,
  sourceNodeIds: string[],
  allNodes: CustomNode[],
): string {
  const sourceNames = sourceNodeIds.map((id) => {
    const node = allNodes.find((n) => n.id === id);
    return getStructDisplayLabel(node?.data?.struct) ?? 'unknown node';
  });
  const joined =
    sourceNames.length <= 2
      ? sourceNames.join(' and ')
      : `${sourceNames.slice(0, -1).join(', ')}, and ${sourceNames[sourceNames.length - 1]}`;
  return `Receiving ${displayName.toLowerCase()} from ${joined}`;
}

// re-enable when publish plug icons are restored
// const PLUG_HOVER_ANIMATION_SPEED = 1.5;
// const PLUG_CLICK_ANIMATION_SPEED = 1.5;
// const ClICK_ANIMATION_DELAY_MS = 10;
// const HOVER_ANIMATION_DELAY_MS = 200;

interface NodeInputRendererProps {
  field: NodeParam;
  handleParameterChange: (
    fieldId: string,
    value: InputValue,
    key?: string,
  ) => void;
  nodeType?: string; // Add node type to skip validation for tool nodes
  nodeId?: string;
  workflowId?: string;
  sessionId?: string;
  isHighlighted?: boolean; // Whether this field should be highlighted
  onHighlightComplete?: () => void; // Callback when highlight animation completes
  isDisabled?: boolean; // Whether inputs should be disabled (read-only mode)
}

/**
 * Parse and clamp an optional integer field value.
 *
 * Empty string is the cleared/unset sentinel used by this renderer, so it
 * must not be treated as a present value (unlike `??`, which would keep it).
 */
function clampOptionalInt(
  rawValue: unknown,
  min: number,
  max: number,
): number | null {
  if (rawValue == null || rawValue === '') {
    return null;
  }
  const numValue =
    typeof rawValue === 'number'
      ? Math.round(rawValue)
      : parseInt(String(rawValue), 10);
  if (isNaN(numValue)) {
    return null;
  }
  const clamped = Math.max(min, Math.min(max, numValue));
  return isNaN(clamped) ? null : clamped;
}

/**
 * Renders individual input fields for nodes with validation support.
 *
 * Validation Rules:
 * - Required fields show a red asterisk (*) and error styling when empty
 * - Default values satisfy required field validation
 * - Different input types have appropriate validation logic:
 *   - Text fields: Must not be empty string
 *   - Numbers: Must be valid numbers
 *   - Booleans: Always valid (true/false)
 *   - Lists: Must have at least one item
 *   - Objects: Must have at least one property
 *   - Dropdowns: Must have a selected value
 */
export const NodeInputRenderer: React.FC<NodeInputRendererProps> = ({
  field,
  handleParameterChange,
  nodeType,
  nodeId,
  workflowId,
  isHighlighted,
  onHighlightComplete,
  isDisabled = false,
}) => {
  const fieldRef = useRef<HTMLDivElement>(null);
  const { nodes, edges, setIsTextareaHovered, setNodes } = useNodeStore();
  const { currentWorkflow } = useWorkflowStore();
  const { fieldErrors, clearMissingFile } = useWorkflowValidationStore();
  const { currentOrg } = useOrgStore();
  const notification = useNotification();
  const startUpload = useUploadStore((state) => state.startUpload);
  const cancelUpload = useUploadStore((state) => state.cancelUpload);
  const removeUpload = useUploadStore((state) => state.removeUpload);
  const fileUpload = useNodeUpload(nodeId ?? '', field.field_id);
  const uploadedFileValue = parseUploadedFile(field.value);
  const showFileUploadState = !!fileUpload && fileUpload.phase !== 'done';
  const evalCatalog = useEvalCatalogStore();

  // State for refreshing options (for governance nodes like Evaluation)
  const [isRefreshingOptions, setIsRefreshingOptions] = useState(false);

  // Dynamic skill options for Code Interpreter skill_id field
  const [skillOptions, setSkillOptions] = useState<string[]>([]);
  const [skillIdMap, setSkillIdMap] = useState<Record<string, string>>({});
  const fieldValueRef = useRef(field.value);
  fieldValueRef.current = field.value;
  useEffect(() => {
    if (field.field_id !== 'skill_id' || !currentOrg?.orgid) {
      setSkillOptions([]);
      setSkillIdMap({});
      return;
    }
    let cancelled = false;
    const fetchSkills = async () => {
      try {
        const { listSkills } = await import('@/services/skills');
        const data = await listSkills();
        if (cancelled) return;
        const skills = data?.skills || [];
        const names = skills.map((s) => s.name);
        const idMap: Record<string, string> = {};
        skills.forEach((s) => {
          idMap[s.name] = s.skill_id;
        });
        setSkillOptions(names);
        setSkillIdMap(idMap);
        if (!fieldValueRef.current && names.length > 0) {
          handleParameterChange(fieldId, idMap[names[0]] || '');
        }
      } catch {
        if (!cancelled) {
          setSkillOptions([]);
          setSkillIdMap({});
        }
      }
    };
    fetchSkills();
    return () => {
      cancelled = true;
    };
  }, [field.field_id, currentOrg?.orgid]);

  // Descriptions for guardrail and eval options
  const [optionDescriptions, setOptionDescriptions] = useState<
    Record<string, string>
  >({});

  // Get node data for prompt enhancer
  const currentNode = nodeId ? nodes.find((n) => n.id === nodeId) : null;
  const nodeStruct = currentNode?.data?.struct;
  const nodeName = nodeStruct?.display_name || '';
  const nodeTypeForEnhancer = nodeStruct?.node_type || nodeType || '';

  // File upload modal state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  // re-enable when publish plug icons are restored
  // const [showPlugHoverAnimation, setShowPlugHoverAnimation] = useState(false);
  // const plugClickLottieRef = useRef<LottieRefCurrentProps | null>(null);
  // const plugHoverLottieRef = useRef<LottieRefCurrentProps | null>(null);

  // Expand modal state
  const [isExpandModalOpen, setIsExpandModalOpen] = useState(false);
  const [expandValue, setExpandValue] = useState<string>('');
  // Track original text for expand modal (needs to be state for reactivity)
  const [expandOriginalText, setExpandOriginalText] = useState<string>('');

  // Local state for textarea to prevent cursor jumping
  const [localTextareaValue, setLocalTextareaValue] = useState<string>(
    String(field.value ?? ''),
  );
  // Track original text for textarea (needs to be state for reactivity)
  const [textareaOriginalText, setTextareaOriginalText] = useState<string>(
    String(field.value ?? ''),
  );

  // Local state for text input to prevent cursor jumping
  const [localInputValue, setLocalInputValue] = useState<string>(
    String(field.value ?? ''),
  );

  // Local state for numeric inputs (FLOAT slider, INTEGER) to prevent jumping
  // during interaction. Each renderer instance handles only one inputType so
  // a single shared state is safe.
  // null  = not yet initialized (fall back to getter)
  // ''    = INTEGER only: user has cleared the field mid-edit
  // number = committed/in-progress numeric value
  const [localNumericValue, setLocalNumericValue] = useState<
    number | string | null
  >(null);

  // Connection detection — declared early so sync effects can use connectionLabel.
  const connectedEdges = field.bindable_config?.input_port?.id
    ? edges.filter(
        (edge) =>
          edge.target === nodeId &&
          edge.targetHandle === field.bindable_config?.input_port?.id,
      )
    : [];
  const isFieldConnected = connectedEdges.length > 0;

  const connectionLabel = isFieldConnected
    ? buildConnectionLabel(
        field.display_name,
        connectedEdges.map((e) => e.source),
        nodes,
      )
    : '';

  // Sync local textarea value with field value changes
  useEffect(() => {
    const currentValue = String(field.value ?? '');
    // Sync if:
    // 1. Value changed externally (not from enhancement) - always sync
    // 2. Value changed during enhancement but it's from an external source (not our enhancement)
    //    We detect this by checking if the change is from enhancement ref
    if (currentValue !== localTextareaValue) {
      if (!isEnhancing || !isChangeFromEnhancementRef.current) {
        // Skip the sync while the user has keystrokes that haven't been
        // committed to the store yet. A stale store write (e.g. from a frozen
        // React Flow onConnectEnd closure) must not silently overwrite text
        // the user is actively editing.
        if (!hasUncommittedEditRef.current) {
          // External change - sync immediately
          setLocalTextareaValue(currentValue);
          // Update original text ref and state for manual edits from external sources
          originalTextRef.current = currentValue;
          setTextareaOriginalText(currentValue);
        }
      }
      // Reset the flag after checking
      isChangeFromEnhancementRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  // Sync local input value with field value changes (for TEXT_BOX)
  useEffect(() => {
    if (field.display_modal === NodeInputType.TEXT_BOX) {
      const currentValue = String(field.value ?? '');
      if (currentValue !== localInputValue) {
        if (
          !isChangeFromUserInputRef.current &&
          !hasUncommittedEditRef.current
        ) {
          // External change - sync immediately
          setLocalInputValue(currentValue);
        }
      }
      // Reset the flag after checking
      isChangeFromUserInputRef.current = false;
    }
  }, [field.value, field.display_modal, localInputValue]);

  // Local state for JSON parsing errors
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Handle highlight effect - scroll to field and show highlight animation
  useEffect(() => {
    if (isHighlighted && fieldRef.current) {
      // Scroll the field into view
      fieldRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Clear the highlight after animation completes (2 seconds)
      const timer = setTimeout(() => {
        onHighlightComplete?.();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [isHighlighted, onHighlightComplete]);

  // Ref for textarea to handle manual scrolling
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Prompt enhancer state - track if enhancement is in progress for sync logic
  const [isEnhancing, setIsEnhancing] = useState(false);

  // Track if the last change was from enhancement (to distinguish from external changes)
  const isChangeFromEnhancementRef = useRef(false);

  // Track if the last change to localInputValue was from user input (to prevent sync loop)
  const isChangeFromUserInputRef = useRef(false);

  // True while the user has keystrokes not yet committed to the store (no blur).
  // Guards sync effects so a stale store write can't overwrite in-progress text.
  const hasUncommittedEditRef = useRef(false);

  // Track original text for prompt enhancer (to detect if already enhanced)
  // This represents the "base" value before any enhancements
  const originalTextRef = useRef<string>(String(field.value ?? ''));

  // Initialize original text ref and state on mount
  useEffect(() => {
    const initialValue = String(field.value ?? '');
    if (!originalTextRef.current && initialValue) {
      originalTextRef.current = initialValue;
      setTextareaOriginalText(initialValue);
    }
  }, [field.value]);

  // Expand modal handlers
  const handleExpandOpen = () => {
    // Use local value for TEXT_BOX to ensure we have the latest user input
    const valueString =
      inputType === NodeInputType.TEXT_BOX
        ? localInputValue
        : inputType === NodeInputType.TEXT_AREA
          ? localTextareaValue
          : String(getCurrentValue() || '');
    setExpandValue(valueString);
    // Set original text state and ref to current value when opening modal
    // This ensures manual edits in the modal are tracked correctly
    if (inputType === NodeInputType.TEXT_AREA) {
      originalTextRef.current = valueString;
      setExpandOriginalText(valueString);
    }
    setIsExpandModalOpen(true);
  };

  const handleExpandClose = () => {
    setIsExpandModalOpen(false);
  };

  const handleExpandSave = () => {
    hasUncommittedEditRef.current = false;
    handleFieldChange(fieldId, expandValue);
    // Update local state to keep it in sync
    if (inputType === NodeInputType.TEXT_BOX) {
      setLocalInputValue(expandValue);
    } else if (inputType === NodeInputType.TEXT_AREA) {
      setLocalTextareaValue(expandValue);
      // Update original text ref and state when user manually edits in expand modal
      if (!isEnhancing) {
        originalTextRef.current = expandValue;
        setExpandOriginalText(expandValue);
        setTextareaOriginalText(expandValue);
      }
    }
    setIsExpandModalOpen(false);
  };

  // File upload handler - now handles single file only
  const handleDropAccepted = async (files: File[]) => {
    // Validate required parameters
    if (!workflowId || !nodeId) {
      console.error('Missing required parameters for file upload:', {
        workflowId,
        nodeId,
      });
      return;
    }

    // Only process the first file for single file upload
    if (files.length === 0) {
      console.error('No files provided for upload');
      return;
    }

    const file = files[0]; // Take only the first file

    // Large files take the slow presigned/streaming path — hand them to the
    // upload store so they run in the background with progress, cancel, and
    // retry on the field, the node, and the floating canvas widget.
    if (isLargeUpload(file)) {
      startUpload({
        nodeId,
        fieldId: field.field_id,
        workflowId,
        file,
        nodeName: nodeName || 'File Input',
      });
      setIsUploadModalOpen(false);
      return;
    }

    // Small files finish quickly via single-shot — keep the simple in-modal
    // spinner rather than the background progress UI.
    setIsUploading(true);
    try {
      const uploadResponse = await uploadFile(file, workflowId, nodeId);
      if (uploadResponse.success && uploadResponse.file) {
        handleParameterChange(field.field_id, uploadResponse);
        if (nodeId) clearMissingFile(nodeId, field.field_id);
        setIsUploadModalOpen(false);
      } else {
        notification.error(
          `File upload failed: ${uploadResponse.message || 'Unknown error'}`,
        );
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred';
      notification.error(`File upload failed: ${errorMessage}`);
    } finally {
      setIsUploading(false);
    }
  };

  const isToolNode = nodeType === NodeCategory.TOOL;
  const fieldId = field.field_id;
  const displayName = field.display_name;
  const inputType = field.display_modal;

  // Get the current value from field.value
  // For objects, we need to handle them specially to avoid [object Object]
  const getCurrentValue = () => {
    const value = field.value;

    // For object fields, return empty string if it's an empty object so placeholder shows
    if (
      inputType === NodeInputType.OBJECT &&
      typeof value === 'object' &&
      value !== null
    ) {
      return Object.keys(value as object).length > 0 ? value : '';
    }

    return value;
  };

  const currentValue = getCurrentValue();

  // For FLOAT fields, ensure we have a valid number and clamp it to min/max
  const getFloatValue = useCallback((): number => {
    const numValue =
      typeof currentValue === 'number'
        ? currentValue
        : parseFloat(String(currentValue));

    if (isNaN(numValue)) {
      return 0;
    }

    // Ensure min and max are always numbers (they might come as strings from JSON)
    const min = Number(field.min_value ?? 0);
    const max = Number(field.max_value ?? 1);
    const clampedValue = Math.max(min, Math.min(max, numValue));
    if (isNaN(clampedValue)) {
      return 0;
    }
    return clampedValue;
  }, [currentValue, field.min_value, field.max_value]);

  // For INTEGER fields, derive the clamped integer from the current (or default)
  // value. Null means the field is genuinely unset — an optional integer with no
  // default renders blank rather than being coerced to a number the user never
  // chose.
  const getIntValue = useCallback((): number | null => {
    const rawValue =
      currentValue == null || currentValue === ''
        ? field.default
        : currentValue;
    return clampOptionalInt(
      rawValue,
      Number(field.min_value ?? 0),
      Number(field.max_value ?? 1000000),
    );
  }, [currentValue, field.default, field.min_value, field.max_value]);

  // Sync local numeric value with field value when field changes externally
  // Track previous currentValue to detect external changes
  const prevCurrentValueRef = useRef(currentValue);
  useEffect(() => {
    if (inputType === NodeInputType.FLOAT) {
      // Only sync when the external value actually changed
      if (prevCurrentValueRef.current !== currentValue) {
        prevCurrentValueRef.current = currentValue;
        const syncedValue = getFloatValue();
        if (!isNaN(syncedValue)) {
          setLocalNumericValue(syncedValue);
        }
      } else if (localNumericValue === null) {
        // Initialize on first render
        const syncedValue = getFloatValue();
        if (!isNaN(syncedValue)) {
          setLocalNumericValue(syncedValue);
        }
      }
    } else if (inputType === NodeInputType.INTEGER) {
      if (prevCurrentValueRef.current !== currentValue) {
        prevCurrentValueRef.current = currentValue;
        setLocalNumericValue(getIntValue() ?? '');
      } else if (localNumericValue === null) {
        // Initialize on first render
        setLocalNumericValue(getIntValue() ?? '');
      }
    }
  }, [inputType, currentValue, getFloatValue, localNumericValue, getIntValue]);

  // For dropdown fields: ensure the stored value is always a valid option.
  // Handles two cases:
  //  1. Value is empty → commit the default or first option so it persists on save.
  //  2. Value is non-empty but doesn't exactly match any option → resolve via
  //     prefix match. This happens when the chat LLM sets a model like
  //     "claude-opus-4-5" but the actual option is "claude-opus-4-5-20251101".
  useEffect(() => {
    if (inputType !== NodeInputType.DROPDOWN) return;
    const options = field.options as string[] | undefined;
    if (!options || options.length === 0) return;

    const strValue = String(currentValue ?? '');
    const isEmpty = strValue === '';

    if (isEmpty) {
      const fallback = field.default || options[0];
      if (fallback) {
        handleParameterChange(fieldId, String(fallback));
      }
      return;
    }

    // Value is non-empty — check if it exactly matches an option
    if (options.includes(strValue)) return;

    // No exact match: try prefix match (e.g. "claude-opus-4-5" → "claude-opus-4-5-20251101")
    const prefixMatch = options.find((opt) => opt.startsWith(strValue));
    if (prefixMatch) {
      handleParameterChange(fieldId, prefixMatch);
      return;
    }

    // Reverse prefix: option is a prefix of the value (unlikely but defensive)
    const reverseMatch = options.find((opt) => strValue.startsWith(opt));
    if (reverseMatch) {
      handleParameterChange(fieldId, reverseMatch);
    }
  }, [
    inputType,
    currentValue,
    field.options,
    field.default,
    fieldId,
    handleParameterChange,
  ]);

  // For INTEGER fields: commit the default value when value is not yet set so it
  // persists on save and is visible in the input (mirrors the DROPDOWN behaviour).
  useEffect(() => {
    if (inputType !== NodeInputType.INTEGER) return;
    if (
      (currentValue == null || currentValue === '') &&
      field.default != null
    ) {
      handleParameterChange(fieldId, field.default);
    }
  }, [inputType, currentValue, field.default, fieldId, handleParameterChange]);

  // Check if this is an Evaluation or Guardrail node that needs a refresh button
  const displayNameLower = nodeStruct?.display_name?.toLowerCase() || '';
  const isEvaluationOrGuardrailNode =
    displayNameLower.includes('evaluation') ||
    displayNameLower.includes('guardrail');

  const isCollectionField = INLINE_COLLECTION_FIELD_IDS.has(fieldId);

  // Fetch descriptions only for MULTI_SELECT fields on eval/guardrail nodes
  // to avoid duplicate API calls from other field renderers on the same node
  const isMultiSelect = inputType === NodeInputType.MULTI_SELECT;
  const shouldFetchDescriptions = isEvaluationOrGuardrailNode && isMultiSelect;

  useEffect(() => {
    if (!shouldFetchDescriptions) return;
    if (displayNameLower.includes('guardrail')) {
      const fetchGuardrailDescriptions = async () => {
        try {
          const res = await getGuardrailsConfigs();
          if (Array.isArray(res?.data)) {
            const map: Record<string, string> = {};
            for (const g of res.data as {
              guardrail_id: string;
              guardrail_description: string;
            }[]) {
              if (g.guardrail_id && g.guardrail_description)
                map[g.guardrail_id] = g.guardrail_description;
            }
            setOptionDescriptions(map);
          }
        } catch (error) {
          Sentry.captureException(error, {
            extra: {
              context: 'fetch-option-descriptions',
              nodeType: displayNameLower,
            },
          });
        }
      };
      fetchGuardrailDescriptions();
    } else if (displayNameLower.includes('evaluation')) {
      setOptionDescriptions(evalCatalog.descriptions);
    }
  }, [shouldFetchDescriptions, displayNameLower, evalCatalog.descriptions]);

  // Sync expand modal value with local textarea value when modal is open
  useEffect(() => {
    if (isExpandModalOpen && inputType === NodeInputType.TEXT_AREA) {
      setExpandValue(localTextareaValue);
    }
  }, [localTextareaValue, isExpandModalOpen, inputType]);

  // const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // const [previewLoading, setPreviewLoading] = useState(false);

  const handleFieldChange = handleParameterChange;

  // Text and textarea inputs keep their value in local state and only commit
  // to the store on blur. Adding a node to the canvas deselects the current
  // node, unmounting this field before blur can fire — so flush any pending
  // local edit to the store on unmount, otherwise in-progress text is lost.
  const flushLocalEditRef = useRef<() => void>(() => {});
  flushLocalEditRef.current = () => {
    const storeValue = String(field.value ?? '');
    if (
      inputType === NodeInputType.TEXT_AREA &&
      localTextareaValue !== storeValue
    ) {
      hasUncommittedEditRef.current = false;
      handleFieldChange(fieldId, localTextareaValue);
    } else if (
      inputType === NodeInputType.TEXT_BOX &&
      localInputValue !== storeValue
    ) {
      hasUncommittedEditRef.current = false;
      handleFieldChange(fieldId, localInputValue);
    }
  };
  useEffect(() => () => flushLocalEditRef.current(), []);

  // Handler to refresh options for Evaluation/Guardrail nodes.
  //
  // Evaluation node: skip the agentic-backend `/list-nodes` catalog (it only
  // knows about v1 evals) and pull the live picker straight from platform-api,
  // merging v1 + v2 eval definitions. This keeps the dropdown in sync the
  // moment a user creates a new eval — no node-list rebuild needed.
  //
  // Guardrail node: keep the original `/list-nodes` flow.
  const handleRefreshOptions = async () => {
    if (!currentOrg?.orgid || !nodeStruct?.display_name || !nodeId) {
      notification.error('Unable to refresh options');
      return;
    }

    setIsRefreshingOptions(true);
    try {
      // The runtime shape of catalog options is `{ option_id, option_display_name }[]`,
      // even though `NodeParam.options` is loosely typed as `string[]` (see the
      // cast-on-read pattern in the MULTI_SELECT renderer below). We mirror that
      // here rather than refactor the global type.
      let nextOptions: NodeParam['options'] | undefined;
      let nextDescriptions: Record<string, string> | undefined;

      if (displayNameLower.includes('evaluation')) {
        await evalCatalog.refresh();
        const evals = [...useEvalCatalogStore.getState().evals];
        evals.sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', undefined, {
            sensitivity: 'base',
          }),
        );
        nextOptions = evals
          .filter((e) => !!e.id)
          .map((e) => ({
            option_id: e.id,
            option_display_name: e.name || e.id,
          })) as unknown as NodeParam['options'];
        nextDescriptions = useEvalCatalogStore.getState().descriptions;
      } else if (isCollectionField) {
        const collectionOptions = await fetchCollectionScopeOptions(fieldId);
        applyRefreshedOptions(nodeId, fieldId, collectionOptions);
        if (
          !collectionOptions ||
          (collectionOptions as unknown[]).length === 0
        ) {
          const entityLabel = isDatasetField(fieldId)
            ? 'datasets'
            : 'collections';
          notification.warning(`No ${entityLabel} found`);
        } else {
          notification.success('Options refreshed');
        }
        return;
      } else {
        const response = await getNodeList(currentOrg.orgid);
        if (response.detail || !response.data?.catalog) {
          throw new Error(response.detail || 'Failed to fetch node list');
        }
        const matchingCatalogNode = response.data.catalog.find(
          (catalogNode) => catalogNode.display_name === nodeStruct.display_name,
        );
        if (!matchingCatalogNode) {
          notification.warning('Could not find matching node definition');
          return;
        }
        const matchingParam = matchingCatalogNode.params.find(
          (param) => param.field_id === fieldId,
        );
        if (!matchingParam?.options) {
          notification.warning('No options found for this field');
          return;
        }
        nextOptions = matchingParam.options;
      }

      if (!nextOptions) {
        notification.warning('No options found for this field');
        return;
      }

      // Update the node's field options in the store
      const updatedNodes = nodes.map((node) => {
        if (node.id !== nodeId) return node;

        const updatedParams = node.data?.struct?.params?.map((param) => {
          if (param.field_id !== fieldId) return param;
          return { ...param, options: nextOptions };
        });

        return {
          ...node,
          data: {
            ...node.data,
            struct: {
              ...node.data?.struct,
              params: updatedParams,
            },
          },
        };
      });

      setNodes(updatedNodes);
      if (nextDescriptions) {
        setOptionDescriptions(nextDescriptions);
      }
      notification.success('Options refreshed successfully');
    } catch (error) {
      console.error('Failed to refresh options:', error);
      notification.error('Failed to refresh options');
    } finally {
      setIsRefreshingOptions(false);
    }
  };

  // Check if the field is required and has a valid value
  const isRequired = field.required;

  // Computed placeholder - shows supplementary info when connected
  const fieldPlaceholder = isFieldConnected
    ? `Add additional instructions...`
    : `Enter ${displayName} ...`;

  // re-enable when publish plug icons are restored
  // const hideMcpPlugButton = field.field_id === 'name';

  // Check if this field has validation errors from the workflow validation store
  // Skip validation checks for tool nodes
  const fieldValidationError =
    !isToolNode && nodeId
      ? fieldErrors.find(
          (error) =>
            error.nodeId === nodeId &&
            (error.fieldName === field.display_name ||
              error.fieldId === field.field_id),
        )
      : null;

  const isFieldValid = isToolNode || !fieldValidationError;

  // Check if the field has a valid value (for error message display)
  const hasValidValue = (() => {
    if (currentValue === undefined || currentValue === null) return false;

    // Handle different input types
    switch (inputType) {
      case NodeInputType.TEXT_BOX:
      case NodeInputType.TEXT_AREA:
      case NodeInputType.SECRET_TEXT_INPUT:
        return String(currentValue).trim() !== '';

      case NodeInputType.INTEGER:
      case NodeInputType.FLOAT: {
        const trimmed = String(currentValue).trim();
        return trimmed !== '' && !isNaN(Number(trimmed));
      }

      case NodeInputType.BOOLEAN:
        return true; // Boolean always has a value (true/false)

      case NodeInputType.LIST:
        return Array.isArray(currentValue)
          ? currentValue.length > 0
          : String(currentValue).trim() !== '';

      case NodeInputType.OBJECT:
        if (typeof currentValue === 'object' && currentValue !== null) {
          return Object.keys(currentValue).length > 0;
        }
        return false;

      case NodeInputType.MULTI_SELECT:
        return Array.isArray(currentValue) ? currentValue.length > 0 : false;

      case NodeInputType.DROPDOWN:
        return (
          currentValue !== '' &&
          currentValue !== null &&
          currentValue !== undefined &&
          String(currentValue).trim() !== ''
        );

      case NodeInputType.MCP_SERVER_CONFIG: {
        if (typeof currentValue === 'object' && currentValue !== null) {
          if ('mcp_servers' in currentValue) {
            const servers = (currentValue as { mcp_servers: unknown[] })
              .mcp_servers;
            return Array.isArray(servers) && servers.length > 0;
          }
          if (Array.isArray(currentValue)) {
            return currentValue.length > 0;
          }
        }
        return false;
      }

      default:
        return String(currentValue).trim() !== '';
    }
  })();

  // Derived values for INTEGER and FLOAT inputs — hoisted to avoid IIFEs in JSX
  const intMin = Number(field.min_value ?? 0);
  const intMax = Number(field.max_value ?? 1000000);
  const intDisplayValue =
    localNumericValue === null ? (getIntValue() ?? '') : localNumericValue;

  const floatMin = Number(field.min_value ?? 0);
  const floatMax = Number(field.max_value ?? 1);
  const floatValue =
    typeof localNumericValue === 'number' ? localNumericValue : getFloatValue();

  return (
    <div
      ref={fieldRef}
      className={classNames('w-full relative overflow-visible mb-4 px-3', {
        'ring-2 ring-blue-500 ring-offset-2 rounded-lg animate-pulse':
          isHighlighted,
      })}
    >
      <div className='flex justify-between items-center mb-1 min-h-[36px]'>
        <div className='flex items-center gap-1 flex-wrap'>
          <Label
            className={classNames('text-sm capitalize', {
              'label-required': isRequired,
            })}
          >
            {displayName}
          </Label>
          {field.info && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className='h-4 w-4 text-muted-foreground cursor-help' />
              </TooltipTrigger>
              <TooltipContent>
                <p className='max-w-xs'>{field.info}</p>
              </TooltipContent>
            </Tooltip>
          )}
          {(inputType === NodeInputType.TEXT_BOX ||
            inputType === NodeInputType.TEXT_AREA) &&
            isFieldConnected &&
            connectionLabel && (
              <span className='flex items-center gap-1 px-1.5 py-0.5 text-xs rounded-md bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 font-normal'>
                <Cable className='size-3 shrink-0' />
                <span>{connectionLabel}</span>
              </span>
            )}
        </div>
        {/* TODO: Re-enable when backend support for MCP/API publishing is complete (JS-1396)
        MCP plug button with Lottie animations - temporarily disabled until backend API is ready

        {!hideMcpPlugButton && (
          <div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='ghost'
                  size='icon'
                  onMouseEnter={() => {
                    const plugHoverAnimation = plugHoverLottieRef.current;
                    // On Hovering an unplugged button, PLAY HOVER ANIMATION
                    if (!field.plugged) {
                      setShowPlugHoverAnimation(true);
                      setTimeout(() => {
                        if (plugHoverAnimation) {
                          plugHoverAnimation.setSpeed(
                            PLUG_HOVER_ANIMATION_SPEED,
                          );
                          plugHoverAnimation.play();
                        }
                      }, HOVER_ANIMATION_DELAY_MS);
                    } else {
                      setShowPlugHoverAnimation(false);
                    }
                  }}
                  onMouseLeave={() => {
                    setShowPlugHoverAnimation(false);
                  }}
                  onClick={() => {
                    setShowPlugHoverAnimation(false);
                    const plugClickAnimation = plugClickLottieRef.current;

                    // NOTE: needs a timeout for animation work...
                    // maybe a delay is needed to ensure Lottie component is ready?
                    setTimeout(() => {
                      if (plugClickAnimation) {
                        if (field.plugged) {
                          // play click animation in reverse when unplugging
                          plugClickAnimation.setDirection(-1);
                        } else {
                          plugClickAnimation.setDirection(1);
                        }
                        // PLAY CLICK ANIMATION
                        plugClickAnimation.setSpeed(PLUG_CLICK_ANIMATION_SPEED);
                        plugClickAnimation.play();
                      }
                    }, ClICK_ANIMATION_DELAY_MS);

                    handleParameterChange(fieldId, !field.plugged, 'plugged');
                  }}
                >
                  <div className='relative w-6 h-6'>
                    <Lottie
                      lottieRef={plugClickLottieRef}
                      animationData={plugClickAnimation}
                      loop={false}
                      autoplay={false}
                      className={`absolute inset-0 ${showPlugHoverAnimation ? 'opacity-0 pointer-events-none' : 'opacity-100'} transition-opacity duration-200`}
                    />
                    <Lottie
                      lottieRef={plugHoverLottieRef}
                      animationData={plugHoverAnimation}
                      loop={true}
                      autoplay={false}
                      className={`absolute inset-0 ${showPlugHoverAnimation ? 'opacity-100' : 'opacity-0 pointer-events-none'} transition-opacity duration-200`}
                    />
                  </div>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Add to MCP/API</p>
              </TooltipContent>
            </Tooltip>
          </div>
        )}
        */}
      </div>

      <div style={{ width: '100%', position: 'relative' }}>
        {inputType === NodeInputType.TEXT_BOX && (
          <div className='relative'>
            <Input
              value={localInputValue}
              onChange={(e) => {
                // Mark this change as from user input to prevent sync loop
                isChangeFromUserInputRef.current = true;
                hasUncommittedEditRef.current = true;
                setLocalInputValue(e.target.value);
              }}
              onBlur={() => {
                hasUncommittedEditRef.current = false;
                handleFieldChange(fieldId, localInputValue);
              }}
              placeholder={fieldPlaceholder}
              disabled={isDisabled}
              className={`${!isFieldValid ? 'border-red-500 focus:border-red-500' : ''} ${isDisabled ? 'opacity-50 cursor-not-allowed pr-8' : 'pr-8'}`}
              style={{
                borderColor: !isFieldValid
                  ? 'var(--color-destructive)'
                  : undefined,
                paddingRight: '32px', // Add space for the expand icon
              }}
            />
            {isDisabled ? (
              <Lock className='absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
            ) : (
              <button
                type='button'
                onClick={handleExpandOpen}
                className='absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors'
                title='Expand to edit'
              >
                <Scan className='h-4 w-4' />
              </button>
            )}
          </div>
        )}

        {inputType === NodeInputType.TEXT_AREA && (
          <div className='relative'>
            <Textarea
              ref={textareaRef}
              value={localTextareaValue}
              onChange={(e) => {
                hasUncommittedEditRef.current = true;
                setLocalTextareaValue(e.target.value);
                if (!isEnhancing) {
                  originalTextRef.current = e.target.value;
                  setTextareaOriginalText(e.target.value);
                }
              }}
              onBlur={() => {
                hasUncommittedEditRef.current = false;
                // Update original text ref and state when user manually edits (not from enhancement)
                if (!isEnhancing) {
                  originalTextRef.current = localTextareaValue;
                  setTextareaOriginalText(localTextareaValue);
                }
                handleFieldChange(fieldId, localTextareaValue);
              }}
              onMouseEnter={() => setIsTextareaHovered(true)}
              onMouseLeave={() => setIsTextareaHovered(false)}
              onWheel={(e) => {
                // NOTE: for some reason, mouse wheel doesn't trigger scroll here
                // my guess is reactFlow is overwriting scroll behavior somehow
                // this code is to force scrolling
                const scrollSensitivity = 0.1;
                if (textareaRef.current) {
                  const scrollAmount = e.deltaY * scrollSensitivity; // Adjust scroll sensitivity
                  textareaRef.current.scrollTop += scrollAmount;
                }
              }}
              rows={8}
              placeholder={fieldPlaceholder}
              disabled={isDisabled}
              className={`nodrag overflow-y-auto ${!isFieldValid ? 'border-red-500 focus:border-red-500' : ''} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={{
                borderColor: !isFieldValid
                  ? 'var(--color-destructive)'
                  : undefined,
                paddingRight: '32px', // Add space for the expand icon
                minHeight: '100px',
                maxHeight: '200px',
                background: 'transparent',
              }}
            />
            {isDisabled ? (
              <Lock className='absolute right-2 top-2 h-4 w-4 text-muted-foreground z-10' />
            ) : (
              <>
                {/* Expand Button */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      onClick={handleExpandOpen}
                      className='absolute right-3 top-2 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors z-10'
                    >
                      <Scan className='h-4 w-4' />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Expand</p>
                  </TooltipContent>
                </Tooltip>
                <PromptEnhancer
                  value={localTextareaValue}
                  onChange={(newValue) => {
                    isChangeFromEnhancementRef.current = true;
                    hasUncommittedEditRef.current = false;
                    setLocalTextareaValue(newValue);
                    handleFieldChange(fieldId, newValue);
                  }}
                  onEnhancementStart={(originalText) => {
                    originalTextRef.current = originalText;
                    setTextareaOriginalText(originalText);
                  }}
                  onEnhancingChange={(enhancing) => {
                    setIsEnhancing(enhancing);
                  }}
                  variant='tooltip'
                  className='absolute right-3 top-7 z-10 pt-1'
                  workflowName={currentWorkflow?.name || ''}
                  workflowDescription={currentWorkflow?.description || ''}
                  nodeType={nodeTypeForEnhancer}
                  nodeName={nodeName}
                  originalText={textareaOriginalText}
                />
              </>
            )}
            {/* Help me write button — opens the Chat tab */}
            {!isFieldConnected && !isDisabled && (
              <button
                type='button'
                onClick={() => {
                  useNodeExplorerStore.getState().openChatTab();
                }}
                className='mt-1 flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors'
              >
                <MessageSquare className='h-3 w-3' />
                Help me write
              </button>
            )}
          </div>
        )}

        {inputType === NodeInputType.SECRET_TEXT_INPUT && (
          <div className='relative'>
            <Input
              type='password'
              value={String(currentValue)}
              onChange={(e) => handleParameterChange(fieldId, e.target.value)}
              placeholder={fieldPlaceholder}
              disabled={isFieldConnected || isDisabled}
              className={`${!isFieldValid ? 'border-red-500 focus:border-red-500' : ''} ${isFieldConnected || isDisabled ? 'opacity-50 cursor-not-allowed pr-8' : ''}`}
              style={{
                borderColor: !isFieldValid
                  ? 'var(--color-destructive)'
                  : undefined,
              }}
            />
            {(isFieldConnected || isDisabled) && (
              <Lock className='absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
            )}
          </div>
        )}

        {inputType === NodeInputType.INTEGER && (
          <div className='relative'>
            <Input
              type='number'
              step={1}
              value={intDisplayValue}
              onKeyDown={(e) => {
                if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9]/g, '');
                if (raw === '') {
                  setLocalNumericValue('');
                  return;
                }
                const num = parseInt(raw, 10);
                if (!isNaN(num)) {
                  setLocalNumericValue(num);
                }
              }}
              onBlur={(e) => {
                const raw = e.target.value.replace(/[^0-9]/g, '');
                if (raw === '') {
                  // A cleared optional field means "unset"; only a field with a
                  // default falls back to it. Parse the default itself — not
                  // getIntValue() — because currentValue is still the previous
                  // number until we commit.
                  if (field.default != null) {
                    const restored = clampOptionalInt(
                      field.default,
                      intMin,
                      intMax,
                    );
                    if (restored != null) {
                      setLocalNumericValue(restored);
                      handleFieldChange(fieldId, restored);
                    }
                    return;
                  }
                  setLocalNumericValue('');
                  if (currentValue != null && currentValue !== '') {
                    handleFieldChange(fieldId, '');
                  }
                  return;
                }
                const num = parseInt(raw, 10);
                if (!isNaN(num)) {
                  const clamped = Math.max(intMin, Math.min(intMax, num));
                  setLocalNumericValue(clamped);
                  handleFieldChange(fieldId, clamped);
                }
              }}
              min={intMin}
              max={intMax}
              disabled={isFieldConnected || isDisabled}
              className={`${!isFieldValid ? 'border-red-500 focus:border-red-500' : ''} ${isFieldConnected || isDisabled ? 'opacity-50 cursor-not-allowed pr-8' : ''}`}
              style={{
                borderColor: !isFieldValid
                  ? 'var(--color-destructive)'
                  : undefined,
              }}
            />
            {(isFieldConnected || isDisabled) && (
              <Lock className='absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
            )}
          </div>
        )}

        {inputType === NodeInputType.FLOAT && (
          <div className='space-y-1'>
            <div className='flex items-center gap-2'>
              <div className='flex-1'>
                <Slider
                  min={floatMin}
                  max={floatMax}
                  step={0.1}
                  value={[floatValue]}
                  onValueChange={(values) => {
                    const clampedValue = Math.max(
                      floatMin,
                      Math.min(floatMax, values[0]),
                    );
                    if (!isNaN(clampedValue)) {
                      setLocalNumericValue(clampedValue);
                    }
                  }}
                  onValueCommit={(values) => {
                    const clampedValue = Math.max(
                      floatMin,
                      Math.min(floatMax, values[0]),
                    );
                    if (!isNaN(clampedValue)) {
                      handleParameterChange(fieldId, clampedValue);
                    }
                  }}
                  disabled={isFieldConnected || isDisabled}
                  className={
                    isFieldConnected || isDisabled
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  }
                />
              </div>
              <div className='relative flex items-center gap-1'>
                <Input
                  type='number'
                  step='0.1'
                  value={floatValue}
                  onChange={(e) => {
                    const numValue = parseFloat(e.target.value);
                    if (!isNaN(numValue)) {
                      const clampedValue = Math.max(
                        floatMin,
                        Math.min(floatMax, numValue),
                      );
                      if (!isNaN(clampedValue)) {
                        setLocalNumericValue(clampedValue);
                      }
                    }
                  }}
                  onBlur={(e) => {
                    const numValue = parseFloat(e.target.value);
                    if (!isNaN(numValue)) {
                      const clampedValue = Math.max(
                        floatMin,
                        Math.min(floatMax, numValue),
                      );
                      if (!isNaN(clampedValue)) {
                        handleParameterChange(fieldId, clampedValue);
                      }
                    }
                  }}
                  min={floatMin}
                  max={floatMax}
                  disabled={isFieldConnected || isDisabled}
                  className={`w-20 ${!isFieldValid ? 'border-red-500 focus:border-red-500' : ''} ${isFieldConnected || isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  style={{
                    borderColor: !isFieldValid
                      ? 'var(--color-destructive)'
                      : undefined,
                  }}
                />
                {isFieldConnected && (
                  <Lock className='absolute -right-6 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                )}
              </div>
            </div>
          </div>
        )}

        {inputType === NodeInputType.BINARY_TOGGLE && (
          <div
            className={`flex items-center space-x-2 ${isFieldConnected || isDisabled ? 'opacity-50' : ''}`}
          >
            <Switch
              id={`${nodeId}-${fieldId}-toggle`}
              checked={currentValue === true || currentValue === 1}
              disabled={isFieldConnected || isDisabled}
              onCheckedChange={(checked) =>
                handleParameterChange(fieldId, checked)
              }
            />
            <Label
              htmlFor={`${nodeId}-${fieldId}-toggle`}
              className={`whitespace-nowrap ${isFieldConnected || isDisabled ? 'cursor-not-allowed' : ''}`}
            >
              {currentValue === true || currentValue === 1 ? 'On' : 'Off'}
            </Label>
          </div>
        )}

        {inputType === NodeInputType.BOOLEAN && (
          <div
            className={`flex items-center space-x-2 ${isFieldConnected || isDisabled ? 'opacity-50' : ''}`}
          >
            <Switch
              id={`${nodeId}-${fieldId}-boolean`}
              checked={!!currentValue}
              disabled={isFieldConnected || isDisabled}
              onCheckedChange={(checked) =>
                handleParameterChange(fieldId, checked)
              }
            />
            <Label
              htmlFor={`${nodeId}-${fieldId}-boolean`}
              className={`whitespace-nowrap ${isFieldConnected || isDisabled ? 'cursor-not-allowed' : ''}`}
            >
              {currentValue ? 'True' : 'False'}
            </Label>
          </div>
        )}

        {inputType === NodeInputType.LIST && (
          <div className='relative'>
            <Textarea
              value={
                Array.isArray(currentValue)
                  ? currentValue.join('\n')
                  : String(currentValue)
              }
              onChange={(e) =>
                handleParameterChange(
                  fieldId,
                  e.target.value.split('\n').map((s) => s.trim()),
                )
              }
              placeholder='Enter values (one per line)'
              rows={8}
              disabled={isFieldConnected || isDisabled}
              className={`${!isFieldValid ? 'border-red-500 focus:border-red-500' : ''} ${isFieldConnected || isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={{
                borderColor: !isFieldValid
                  ? 'var(--color-destructive)'
                  : undefined,
              }}
            />
            {(isFieldConnected || isDisabled) && (
              <Lock className='absolute right-2 top-2 h-4 w-4 text-muted-foreground' />
            )}
          </div>
        )}

        {inputType === NodeInputType.DROPDOWN &&
          (field.field_id === 'skill_id'
            ? skillOptions.length > 0
            : !!field.options) && (
            <div
              className={`flex items-center gap-2 ${isFieldConnected || isDisabled ? 'opacity-50' : ''}`}
            >
              <div
                className={`w-full dark:bg-input/30 ${!isFieldValid ? 'border-destructive focus:border-destructive' : ''}`}
                style={{
                  borderColor: !isFieldValid
                    ? 'var(--color-destructive)'
                    : undefined,
                }}
              >
                <Select
                  options={
                    field.field_id === 'skill_id'
                      ? skillOptions
                      : (field.options as string[])
                  }
                  selected={
                    field.field_id === 'skill_id'
                      ? currentValue
                        ? Object.entries(skillIdMap).find(
                            ([, id]) => id === currentValue,
                          )?.[0] || String(currentValue)
                        : skillOptions[0]
                      : currentValue
                        ? String(currentValue)
                        : (field.options as string[])?.[0]
                  }
                  disabled={isFieldConnected || isDisabled}
                  className='w-full h-auto'
                  onSelect={(value) => {
                    if (
                      field.field_id === 'skill_id' &&
                      skillIdMap[value || '']
                    ) {
                      handleParameterChange(fieldId, skillIdMap[value || '']);
                    } else {
                      handleParameterChange(fieldId, value || '');
                    }
                  }}
                  allowAllOption={false}
                />
              </div>
              {/* {fieldId === 'voice' && (
                <div>
                  {previewLoading ? (
                    <LoadingSpinner />
                  ) : (
                    <AudioPlayer
                      audioDataUrl={previewUrl || ''}
                      disabled={previewLoading || !previewUrl}
                    />
                  )}
                </div>
              )} */}
            </div>
          )}

        {inputType === NodeInputType.MULTI_SELECT && (
          <div
            className={`flex items-center gap-2 ${isFieldConnected || isDisabled ? 'opacity-50' : ''}`}
          >
            <div
              className={`w-full ${!isFieldValid ? 'border-destructive focus:border-destructive' : ''}`}
              style={{
                borderColor: !isFieldValid
                  ? 'var(--color-destructive)'
                  : undefined,
              }}
            >
              {field.options && field.options.length > 0 ? (
                <MultiSelect
                  items={field.options}
                  selectedValues={
                    Array.isArray(currentValue)
                      ? currentValue.map((item) => {
                          // Convert stored objects to string IDs for MultiSelect
                          if (typeof item === 'string') {
                            return item;
                          }
                          if (
                            typeof item === 'object' &&
                            item !== null &&
                            'option_id' in item
                          ) {
                            return (item as { option_id: string }).option_id;
                          }
                          return String(item);
                        })
                      : []
                  }
                  onSelectionChange={(values) => {
                    // Convert string IDs back to option objects
                    if (!field.options) return;

                    const selectedObjects = values
                      .map((valueId) => {
                        const opt = field.options!.find((option) => {
                          if (typeof option === 'string') {
                            return option === valueId;
                          }
                          if (
                            typeof option === 'object' &&
                            option !== null &&
                            'option_id' in option
                          ) {
                            return (
                              (option as { option_id: string }).option_id ===
                              valueId
                            );
                          }
                          return false;
                        });
                        if (opt !== undefined) return opt;

                        // ID not in current options (e.g. a hidden eval type
                        // that was attached before the type was hidden) —
                        // preserve the original stored object so it isn't
                        // silently dropped on re-edit.
                        // Cast matches the existing cast-on-read pattern used
                        // throughout this component (options are objects at
                        // runtime but typed as string[]).
                        const existing = Array.isArray(currentValue)
                          ? (currentValue as unknown as string[]).find(
                              (item) => {
                                if (typeof item === 'string')
                                  return item === valueId;
                                if (
                                  typeof item === 'object' &&
                                  item !== null &&
                                  'option_id' in item
                                ) {
                                  return (
                                    (item as unknown as { option_id: string })
                                      .option_id === valueId
                                  );
                                }
                                return false;
                              },
                            )
                          : undefined;
                        return existing;
                      })
                      .filter(
                        (item): item is NonNullable<typeof item> =>
                          item !== undefined,
                      );

                    handleFieldChange(fieldId, selectedObjects);
                  }}
                  getItemValue={(item) => {
                    // Handle both string options and object options
                    if (typeof item === 'string') {
                      return item;
                    }
                    if (
                      typeof item === 'object' &&
                      item !== null &&
                      'option_id' in item
                    ) {
                      return (item as { option_id: string }).option_id;
                    }
                    return String(item);
                  }}
                  getItemLabel={(item) => {
                    // Handle both string options and object options
                    if (typeof item === 'string') {
                      return item;
                    }
                    if (
                      typeof item === 'object' &&
                      item !== null &&
                      'option_display_name' in item
                    ) {
                      return (item as { option_display_name: string })
                        .option_display_name;
                    }
                    return String(item);
                  }}
                  getItemDescription={
                    Object.keys(optionDescriptions).length > 0
                      ? (item) => {
                          if (
                            typeof item === 'object' &&
                            item !== null &&
                            'option_id' in item
                          ) {
                            return (
                              optionDescriptions[
                                (item as { option_id: string }).option_id
                              ] || ''
                            );
                          }
                          return '';
                        }
                      : undefined
                  }
                  placeholder={field.placeholder || 'Select items...'}
                  searchPlaceholder='Search options...'
                  emptyMessage='No options found.'
                  disabled={isFieldConnected || isDisabled}
                  buttonClassName='w-full justify-between'
                  singularLabel='item'
                  pluralLabel='items'
                />
              ) : (
                <div className='flex items-center justify-center h-10 px-3 py-2 text-sm text-muted-foreground border border-input rounded-md bg-muted/50'>
                  <span>No options available</span>
                </div>
              )}
            </div>
            {/* Refresh button for Evaluation, Guardrail, and Collection/Dataset nodes */}
            {(isEvaluationOrGuardrailNode || isCollectionField) &&
              !isFieldConnected &&
              !isDisabled && (
                <Button
                  variant='outline'
                  size='sm'
                  onClick={handleRefreshOptions}
                  disabled={isRefreshingOptions}
                  className='shrink-0 size-9 p-0'
                >
                  {isRefreshingOptions ? (
                    <LoadingSpinner className='size-4' />
                  ) : (
                    <RefreshCw className='size-5' />
                  )}
                </Button>
              )}
            {(isFieldConnected || isDisabled) && (
              <Lock className='absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
            )}
          </div>
        )}

        {inputType === NodeInputType.OBJECT && (
          <div className='relative'>
            <Textarea
              value={
                typeof currentValue === 'object' && currentValue !== null
                  ? JSON.stringify(currentValue, null, 2)
                  : ''
              }
              onChange={(e) => {
                if (isFieldConnected || isDisabled) return;
                try {
                  const parsed = JSON.parse(e.target.value);
                  handleParameterChange(fieldId, parsed);
                  setJsonError(null); // Clear error on successful parse
                } catch {
                  // Don't update the value if JSON is invalid
                  // This allows the user to continue typing and fix the error
                  setJsonError('Invalid JSON format');
                }
              }}
              onBlur={(e) => {
                if (isFieldConnected || isDisabled) return;

                try {
                  const parsed = JSON.parse(e.target.value);
                  if (JSON.stringify(parsed) !== JSON.stringify(currentValue)) {
                    handleParameterChange(fieldId, parsed);
                  }
                  setJsonError(null); // Clear error on successful parse
                } catch (error) {
                  // Show more detailed error on blur
                  const errorMessage =
                    error instanceof Error
                      ? error.message
                      : 'Invalid JSON format';
                  setJsonError(errorMessage);
                  notification.error(`Invalid JSON: ${errorMessage}`);
                }
              }}
              onFocus={() => {
                // Clear JSON error when user starts editing again
                setJsonError(null);
              }}
              rows={8}
              placeholder='Enter JSON object'
              disabled={isFieldConnected || isDisabled}
              className={`${!isFieldValid || jsonError ? 'border-red-500 focus:border-red-500' : ''} ${isFieldConnected || isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={{
                borderColor:
                  !isFieldValid || jsonError
                    ? 'var(--color-destructive)'
                    : undefined,
              }}
            />
            {jsonError && !isFieldConnected && !isDisabled && (
              <p className='text-sm text-red-500 mt-1'>{jsonError}</p>
            )}
            {(isFieldConnected || isDisabled) && (
              <Lock className='absolute right-2 top-2 h-4 w-4 text-muted-foreground' />
            )}
          </div>
        )}

        {inputType === NodeInputType.FILE_UPLOAD && (
          <>
            {showFileUploadState && fileUpload ? (
              <PanelUploadField
                upload={fileUpload}
                onCancel={() => cancelUpload(fileUpload.uploadId)}
                onDismiss={() => removeUpload(fileUpload.uploadId)}
              />
            ) : uploadedFileValue ? (
              <PanelUploadSuccess
                file={uploadedFileValue}
                canRemove={!isFieldConnected && !isDisabled}
                onRemove={() => {
                  handleParameterChange(field.field_id, '');
                  if (nodeId) clearMissingFile(nodeId, field.field_id);
                }}
              />
            ) : (
              <div className='relative'>
                <Button
                  variant='outline'
                  onClick={() => setIsUploadModalOpen(true)}
                  className={`w-full flex items-center gap-2 ${isFieldConnected || isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={
                    !workflowId || !nodeId || isFieldConnected || isDisabled
                  }
                >
                  <Upload className='h-4 w-4' />
                  Upload File
                </Button>
                {(isFieldConnected || isDisabled) && (
                  <Lock className='absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                )}
              </div>
            )}
            {(!workflowId || !nodeId) && (
              <p className='text-sm text-muted-foreground mt-1'>
                File upload requires workflow context
              </p>
            )}
            <Modal
              isDialogOpen={isUploadModalOpen}
              setIsDialogOpen={setIsUploadModalOpen}
              title={<span>Upload File</span>}
              footer={
                <Button
                  variant='outline'
                  onClick={() => setIsUploadModalOpen(false)}
                  disabled={isUploading}
                >
                  Cancel
                </Button>
              }
            >
              <div className='flex flex-col gap-4 mt-4'>
                {isUploading ? (
                  <div className='flex flex-col items-center justify-center py-8 gap-4'>
                    <LoadingSpinner />
                    <span className='text-muted-foreground'>
                      Uploading file...
                    </span>
                  </div>
                ) : (
                  <div className='space-y-2'>
                    <p className='text-sm text-muted-foreground'>
                      Select a single file to upload
                    </p>
                    <Dropzone
                      handleUpload={handleDropAccepted}
                      isLoading={isUploading}
                      acceptType={FILE_INPUT_ACCEPT}
                      maxSize={FILE_INPUT_MAX_SIZE_MB}
                      audioMaxSize={FILE_INPUT_MEDIA_MAX_SIZE_MB}
                      multiple={false}
                      preventCanvasInterference={true}
                    />
                  </div>
                )}
              </div>
            </Modal>
          </>
        )}

        {/* Expand Modal for Text Input and Text Area */}
        {(inputType === NodeInputType.TEXT_BOX ||
          inputType === NodeInputType.TEXT_AREA) && (
          <Modal
            isDialogOpen={isExpandModalOpen}
            setIsDialogOpen={setIsExpandModalOpen}
            title={<span>Edit {displayName}</span>}
            footer={
              <div className='flex gap-2'>
                <Button variant='outline' onClick={handleExpandClose}>
                  Cancel
                </Button>
                <Button onClick={handleExpandSave}>Save</Button>
              </div>
            }
          >
            <div className='flex flex-col gap-4 mt-4 max-h-[70vh] overflow-hidden relative'>
              {inputType === NodeInputType.TEXT_BOX ? (
                <Input
                  value={expandValue}
                  onChange={(e) => {
                    setExpandValue(e.target.value);
                    setLocalInputValue(e.target.value);
                    handleFieldChange(fieldId, e.target.value);
                  }}
                  placeholder={`Enter ${displayName} ...`}
                  className='w-full'
                  autoFocus
                />
              ) : (
                <div className='relative'>
                  <Textarea
                    value={expandValue}
                    onChange={(e) => {
                      setExpandValue(e.target.value);
                      setLocalTextareaValue(e.target.value);
                      handleFieldChange(fieldId, e.target.value);
                      if (!isEnhancing) {
                        originalTextRef.current = e.target.value;
                        setExpandOriginalText(e.target.value);
                      }
                    }}
                    placeholder={`Enter ${displayName} ...`}
                    className='w-full min-h-[300px] max-h-[60vh] resize-y overflow-y-auto pr-6'
                    autoFocus
                  />
                  {/* Prompt Enhancer in Modal - with sliding label */}
                  <PromptEnhancer
                    value={expandValue}
                    onChange={(newValue) => {
                      // Mark this change as from enhancement
                      isChangeFromEnhancementRef.current = true;
                      setExpandValue(newValue);
                      setLocalTextareaValue(newValue);
                      handleFieldChange(fieldId, newValue);
                    }}
                    onEnhancementStart={(originalText) => {
                      // Capture original text BEFORE enhancement starts
                      // This ensures we get the correct value before the reverse animation begins
                      originalTextRef.current = originalText;
                      setExpandOriginalText(originalText);
                    }}
                    onEnhancingChange={(enhancing) => {
                      setIsEnhancing(enhancing);
                    }}
                    variant='slide'
                    className='absolute right-1 top-1 z-10'
                    workflowName={currentWorkflow?.name || ''}
                    workflowDescription={currentWorkflow?.description || ''}
                    nodeType={nodeTypeForEnhancer}
                    nodeName={nodeName}
                    originalText={expandOriginalText}
                  />
                </div>
              )}
            </div>
          </Modal>
        )}

        {inputType === NodeInputType.MCP_SERVER_CONFIG && (
          <MCPServersInput
            field={field}
            fieldId={fieldId}
            isFieldConnected={isFieldConnected}
            isDisabled={isDisabled}
            handleFieldChange={handleFieldChange}
          />
        )}

        {inputType === NodeInputType.AUDIO && (
          <div className='relative'>
            <Input
              type='file'
              onChange={(e) => handleParameterChange(fieldId, e.target.value)}
              disabled={isFieldConnected || isDisabled}
              className={
                isFieldConnected || isDisabled
                  ? 'opacity-50 cursor-not-allowed pr-8'
                  : ''
              }
            />
            {(isFieldConnected || isDisabled) && (
              <Lock className='absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
            )}
          </div>
        )}
      </div>

      {/* Show error message for required fields that are empty (skip for tool nodes) */}
      {isRequired && !hasValidValue && fieldValidationError && !isToolNode && (
        <div className='text-red-500 text-sm mt-1 flex items-center gap-1'>
          <span>⚠</span>
          <span>{fieldValidationError.message}</span>
        </div>
      )}
    </div>
  );
};
